import {getMergeField} from '../config/mergeFields';
import type {MergeContext, PlateBlockNode, PlateInlineNode, PlateValue} from './types';

type PlateNode = PlateBlockNode | PlateInlineNode;

const isMention = (node: PlateNode): node is Extract<PlateInlineNode, {type: 'mention'}> =>
	'type' in node && node.type === 'mention';

// Any non-mention node carrying children is a container to recurse into — matching on structure
// rather than a list of known block types keeps list-item wrappers (and whatever the editor adds
// next) from smuggling unresolved mentions past the walk.
const isContainer = (node: PlateNode): node is PlateBlockNode => !isMention(node) && 'children' in node;

function resolveNode(node: PlateNode, ctx: MergeContext): PlateNode {
	if (isMention(node)) {
		const field = getMergeField(node.value);
		if (!field) return {text: ''};
		try {
			return {text: field.resolve(ctx)};
		} catch {
			return {text: ''};
		}
	}
	if (isContainer(node)) {
		const children = (node.children as PlateNode[]).map(child => resolveNode(child, ctx));
		return {...node, children: children as PlateBlockNode['children']};
	}
	return node;
}

export function resolvePlateValue(value: PlateValue, ctx: MergeContext): PlateValue {
	return value.map(block => resolveNode(block, ctx) as PlateBlockNode);
}

/**
 * The text leaves under a node, in order, with list-item-content wrappers (and any other
 * container nesting) flattened away. Mentions are kept as leaves so unresolved tokens stay
 * visible. This is what renderers iterate for a block's inline content.
 */
export function plateTextLeaves(node: PlateNode): PlateInlineNode[] {
	if (isContainer(node)) {
		return (node.children as PlateNode[]).flatMap(child => plateTextLeaves(child));
	}
	return [node as PlateInlineNode];
}
